/**
 * Insights Page
 *
 * Parent dashboard analytics view. Markup mirrors the loveable
 * DashboardInsightsPage exactly; every tab is wired to real database data.
 */

import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useChildProfiles } from '@/hooks/queries/useChildProfiles';
import {
  useActivityTimeline,
  useWeeklySummary,
  useChatTopics,
  useTopicMessages,
  useJourneyProgressAnalytics,
  useAnalyticsOverview,
} from '@/hooks/queries/useAnalytics';
import { useChatSessions, useSessionMessages } from '@/hooks/queries/useBuddyChat';
import { useMoodHistory } from '@/hooks/queries/useMoodCheckin';
import { useChildSharedMoments } from '@/hooks/queries/useSharedMoments';
import { LoadingSpinner } from '@/components/shared';
import { ConversationTranscriptModal } from '@/components/dashboard/insights';
import {
  ChevronDown,
  MessageCircle,
  MessagesSquare,
  BookOpen,
  Map,
  Heart,
  Eye,
} from 'lucide-react';
import type { MoodType } from '@/services/moodCheckin';
import type { SharedMomentType } from '@/services/sharedMoments';

type Tab = 'topics' | 'conversations' | 'mood' | 'activity' | 'journeys' | 'moments';

const VALID_TABS: Tab[] = ['topics', 'conversations', 'mood', 'activity', 'journeys', 'moments'];

const MOOD_EMOJI: Record<MoodType, string> = {
  happy: '😊',
  sad: '😢',
  angry: '😠',
  scared: '😨',
  calm: '😌',
  worried: '😟',
  tired: '😴',
  excited: '🤩',
  notsure: '🤔',
};

const MOOD_LABEL: Record<MoodType, string> = {
  happy: 'Happy',
  sad: 'Sad',
  angry: 'Angry',
  scared: 'Scared',
  calm: 'Calm',
  worried: 'Worried',
  tired: 'Tired',
  excited: 'Excited',
  notsure: 'Not sure',
};

const MOMENT_COLORS: Record<string, { border: string; bg: string }> = {
  journey_complete: { border: '#E8946A', bg: '#FEF0EA' },
  story_created: { border: '#B8A5D4', bg: '#F3EFF8' },
  story_read: { border: '#B8A5D4', bg: '#F3EFF8' },
  curiosity: { border: '#3ECDC6', bg: '#E8F6F4' },
  family_listen: { border: '#D4A843', bg: '#FDF6E8' },
  mood_checkin: { border: '#9B978E', bg: '#F5F3EE' },
};

const JOURNEY_PALETTE = ['#B8A5D4', '#E8946A', '#3ECDC6', '#D4A843'];

function formatShortDay(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'short' });
}

function formatLongDay(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function InsightsPage() {
  const { user } = useAuth();
  const { data: children = [], isLoading: childrenLoading } = useChildProfiles(user?.id);

  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get('tab') as Tab | null;
  const [activeTab, setActiveTab] = useState<Tab>(
    urlTab && VALID_TABS.includes(urlTab) ? urlTab : 'topics'
  );

  const [selectedChildId, setSelectedChildId] = useState<string>('');

  const { data: overview } = useAnalyticsOverview();

  const activeChildId = selectedChildId || children[0]?.id || '';
  const selectedChild = children.find((c) => c.id === activeChildId);

  // All data queries scoped to the active child
  const { data: activityTimeline = [] } = useActivityTimeline(activeChildId, 7);
  const { data: weeklySummary } = useWeeklySummary(activeChildId);
  const { data: chatTopics = [] } = useChatTopics(activeChildId, 5);
  const { data: journeyProgress = [] } = useJourneyProgressAnalytics(activeChildId);
  const { data: moodHistory = [] } = useMoodHistory(activeChildId, 14);
  const { data: childMoments = [] } = useChildSharedMoments(activeChildId);

  // Conversation history (verbatim transcripts) + topic drill-down
  const { data: chatSessions = [], isLoading: sessionsLoading } = useChatSessions(activeChildId, true);
  const [openSessionId, setOpenSessionId] = useState<string | null>(null);
  const [drillTopic, setDrillTopic] = useState<string | null>(null);
  const { data: sessionMessages = [], isLoading: sessionMessagesLoading } = useSessionMessages(
    activeChildId,
    openSessionId ?? undefined
  );
  const { data: topicMessages = [], isLoading: topicMessagesLoading } = useTopicMessages(
    activeChildId,
    drillTopic ?? undefined
  );
  const openSession = chatSessions.find((s) => s.id === openSessionId);

  // Metric 1: Questions asked this week (weekly summary)
  const questionsAsked = parseInt(String(weeklySummary?.total_messages ?? '0'), 10);
  const questionsPerDay = Math.round(questionsAsked / 7);

  // Metric 2: Stories created this week
  const storiesCreated = parseInt(String(weeklySummary?.total_stories ?? '0'), 10);

  // Metric 3: Most recently active journey
  const activeJourney = useMemo(
    () => journeyProgress.find((j) => j.status === 'active'),
    [journeyProgress]
  );

  // Metric 4: Family memories count from overview
  const familyMemories = overview?.family_memories ?? 0;

  // Topics tab: top 5 topics by mention count
  const maxTopicCount = useMemo(
    () => Math.max(1, ...chatTopics.map((t) => t.mention_count)),
    [chatTopics]
  );
  const curriculumAreas = useMemo(() => {
    const set = new Set<string>();
    chatTopics.forEach((t) => {
      if (t.category_name) set.add(t.category_name);
    });
    return Array.from(set);
  }, [chatTopics]);

  // Mood tab: 14 days, oldest → newest, with empty placeholders for missing days
  const moodGrid = useMemo(() => {
    const items: { day: string; emoji: string | null; isToday: boolean }[] = [];

    // Use local YYYY-MM-DD strings for keys to avoid UTC/local timezone shift.
    const localKey = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    // Map backend entries by their date string (already YYYY-MM-DD from server)
    const moodByDate: Record<string, MoodType> = {};
    moodHistory.forEach((entry) => {
      // entry.date is "YYYY-MM-DD" from the server — use as-is
      if (!moodByDate[entry.date]) moodByDate[entry.date] = entry.mood;
    });

    const today = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = localKey(d);
      const mood = moodByDate[key];
      items.push({
        day: d.toLocaleDateString('en-US', { weekday: 'short' }),
        emoji: mood ? (MOOD_EMOJI[mood] ?? null) : null,
        isToday: i === 0,
      });
    }
    return items;
  }, [moodHistory]);

  // Most common mood
  const moodStats = useMemo(() => {
    if (moodHistory.length === 0) return null;
    const counts: Partial<Record<MoodType, number>> = {};
    moodHistory.forEach((m) => {
      counts[m.mood] = (counts[m.mood] ?? 0) + 1;
    });
    const sorted = (Object.entries(counts) as [MoodType, number][]).sort(
      (a, b) => b[1] - a[1]
    );
    const top = sorted[0];
    const transitions = [...moodHistory]
      .slice(0, 4)
      .reverse()
      .map((m) => MOOD_LABEL[m.mood]);
    return {
      topMood: top[0],
      topCount: top[1],
      totalDays: moodHistory.length,
      transitions,
    };
  }, [moodHistory]);

  // Activity tab: use real session_duration_minutes for the bar chart
  const maxMinutes = useMemo(
    () => Math.max(1, ...activityTimeline.map((d) => d.session_duration_minutes)),
    [activityTimeline]
  );

  // Journeys tab
  const activeJourneys = journeyProgress.filter((j) => j.status === 'active');
  const completedJourneys = journeyProgress.filter((j) => j.status === 'completed');

  // Weekly summary sentence — computed client-side
  const weeklySummarySentence = useMemo(() => {
    if (!selectedChild) return '';
    const parts: string[] = [];
    if (chatTopics.length > 0) {
      const topTopic = chatTopics[0].topic;
      parts.push(`was most curious about ${topTopic}`);
    }
    if (storiesCreated > 0) {
      parts.push(`created ${storiesCreated} ${storiesCreated === 1 ? 'story' : 'stories'}`);
    }
    if (activeJourney) {
      parts.push(
        `reached step ${activeJourney.steps_completed} of ${activeJourney.total_steps} of ${activeJourney.title}`
      );
    }
    if (moodStats) {
      parts.push(`checked in as ${MOOD_LABEL[moodStats.topMood].toLowerCase()} most days`);
    }

    if (parts.length === 0) {
      return `This week, ${selectedChild.name} hasn't logged much activity yet — that's okay. A gentle prompt at check-in time can help.`;
    }
    return `This week, ${selectedChild.name} ${parts.join(', and ')}.`;
  }, [selectedChild, chatTopics, storiesCreated, activeJourney, moodStats]);

  if (childrenLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div
        className="rounded-2xl p-10 text-center"
        style={{ background: '#FFFFFF', border: '1px solid #E8E6E1' }}
      >
        <p className="text-[15px]" style={{ color: '#6B675E' }}>
          Add a child profile to start seeing insights.
        </p>
      </div>
    );
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'topics', label: 'Topics' },
    { key: 'conversations', label: 'Conversations' },
    { key: 'mood', label: 'Mood' },
    { key: 'activity', label: 'Activity' },
    { key: 'journeys', label: 'Journeys' },
    { key: 'moments', label: 'Moments' },
  ];

  return (
    <div className="dashboard-scope">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[28px] mb-1" style={{ fontWeight: 600, color: '#2A2926' }}>
            Insights
          </h1>
          <p className="text-[15px] max-w-xl" style={{ color: '#6B675E' }}>
            Understand what your child is curious about, how they're feeling, and how
            they're growing — without hovering.
          </p>
        </div>
        <div className="relative shrink-0">
          <select
            value={activeChildId}
            onChange={(e) => {
              setSelectedChildId(e.target.value);
              setOpenSessionId(null);
              setDrillTopic(null);
            }}
            className="appearance-none pl-4 pr-9 py-2.5 rounded-lg text-[14px] border cursor-pointer"
            style={{
              background: '#FFFFFF',
              borderColor: '#E8E6E1',
              color: '#2A2926',
              fontWeight: 500,
            }}
          >
            {children.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <ChevronDown
            size={14}
            className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ color: '#9B978E' }}
          />
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="rounded-2xl p-5" style={{ background: '#E8F6F4' }}>
          <div className="flex items-center gap-2 mb-2">
            <MessageCircle size={15} style={{ color: '#3ECDC6' }} />
            <span className="text-[13px]" style={{ fontWeight: 500, color: '#6B675E' }}>
              Questions asked
            </span>
          </div>
          <p className="text-[32px] mb-0.5" style={{ fontWeight: 700, color: '#2A2926' }}>
            {questionsAsked}
          </p>
          <p className="text-[12px]" style={{ color: '#9B978E' }}>
            this week · ~{questionsPerDay} per day
          </p>
        </div>
        <div className="rounded-2xl p-5" style={{ background: '#F3EFF8' }}>
          <div className="flex items-center gap-2 mb-2">
            <BookOpen size={15} style={{ color: '#B8A5D4' }} />
            <span className="text-[13px]" style={{ fontWeight: 500, color: '#6B675E' }}>
              Stories created
            </span>
          </div>
          <p className="text-[32px] mb-0.5" style={{ fontWeight: 700, color: '#2A2926' }}>
            {storiesCreated}
          </p>
          <p className="text-[12px]" style={{ color: '#9B978E' }}>
            this week
          </p>
        </div>
        <div className="rounded-2xl p-5" style={{ background: '#FEF0EA' }}>
          <div className="flex items-center gap-2 mb-2">
            <Map size={15} style={{ color: '#E8946A' }} />
            <span className="text-[13px]" style={{ fontWeight: 500, color: '#6B675E' }}>
              Journey progress
            </span>
          </div>
          {activeJourney ? (
            <>
              <p className="text-[28px] mb-0.5" style={{ fontWeight: 700, color: '#2A2926' }}>
                Step {activeJourney.steps_completed} of {activeJourney.total_steps}
              </p>
              <p className="text-[12px]" style={{ color: '#9B978E' }}>
                {activeJourney.title}
              </p>
            </>
          ) : (
            <>
              <p className="text-[28px] mb-0.5" style={{ fontWeight: 700, color: '#2A2926' }}>
                —
              </p>
              <p className="text-[12px]" style={{ color: '#9B978E' }}>
                No active journey
              </p>
            </>
          )}
        </div>
        <div className="rounded-2xl p-5" style={{ background: '#FDF6E8' }}>
          <div className="flex items-center gap-2 mb-2">
            <Heart size={15} style={{ color: '#D4A843' }} />
            <span className="text-[13px]" style={{ fontWeight: 500, color: '#6B675E' }}>
              Family moments
            </span>
          </div>
          <p className="text-[32px] mb-0.5" style={{ fontWeight: 700, color: '#2A2926' }}>
            {familyMemories}
          </p>
          <p className="text-[12px]" style={{ color: '#9B978E' }}>
            memories saved
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div
        className="flex gap-1 p-1 rounded-lg mb-6 overflow-x-auto w-fit"
        style={{ background: '#F5F3EE' }}
      >
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className="px-4 py-2 rounded-md text-[13px] whitespace-nowrap transition"
            style={{
              fontWeight: activeTab === t.key ? 600 : 400,
              color: activeTab === t.key ? '#2A2926' : '#6B675E',
              background: activeTab === t.key ? '#FFFFFF' : 'transparent',
              boxShadow:
                activeTab === t.key ? '0 1px 3px rgba(42,41,38,0.08)' : 'none',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Topics Tab */}
      {activeTab === 'topics' && (
        <div
          className="rounded-2xl p-6 mb-8"
          style={{ background: '#FFFFFF', border: '1px solid #E8E6E1' }}
        >
          <h2 className="text-[20px] mb-1" style={{ fontWeight: 600, color: '#2A2926' }}>
            What they're curious about
          </h2>
          <p className="text-[14px] mb-6" style={{ color: '#9B978E' }}>
            Topics your child explored this week
          </p>
          {chatTopics.length === 0 ? (
            <p className="text-[14px] py-8 text-center" style={{ color: '#9B978E' }}>
              No topics yet — they'll appear as your child explores with Luno.
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-3 mb-6">
                {chatTopics.map((t, i) => (
                  <button
                    key={t.topic}
                    type="button"
                    onClick={() => setDrillTopic(t.topic)}
                    title={`See the questions ${selectedChild?.name ?? 'your child'} asked about ${t.topic}`}
                    className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition text-left w-full"
                  >
                    <span
                      className="text-[13px] w-5 text-right"
                      style={{ color: '#9B978E' }}
                    >
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className="text-[14px]"
                          style={{ fontWeight: 500, color: '#2A2926' }}
                        >
                          {t.topic}
                        </span>
                        <span className="text-[12px] underline decoration-dotted" style={{ color: '#3ECDC6' }}>
                          {t.mention_count} · see questions
                        </span>
                      </div>
                      <div className="h-2 rounded-full" style={{ background: '#F5F3EE' }}>
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${(t.mention_count / maxTopicCount) * 100}%`,
                            background: '#3ECDC6',
                          }}
                        />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              {curriculumAreas.length > 0 && (
                <div>
                  <p className="text-[13px] mb-2" style={{ color: '#9B978E' }}>
                    Curriculum areas touched this week:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {curriculumAreas.map((c) => (
                      <span
                        key={c}
                        className="px-3 py-1 rounded-full text-[12px]"
                        style={{ background: '#F5F3EE', color: '#6B675E' }}
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Conversations Tab */}
      {activeTab === 'conversations' && (
        <div
          className="rounded-2xl p-6 mb-8"
          style={{ background: '#FFFFFF', border: '1px solid #E8E6E1' }}
        >
          <h2 className="text-[20px] mb-1" style={{ fontWeight: 600, color: '#2A2926' }}>
            Conversation history
          </h2>
          <p className="text-[14px] mb-6" style={{ color: '#9B978E' }}>
            Open any chat to read exactly what {selectedChild?.name ?? 'your child'} asked Luno.
          </p>
          {sessionsLoading ? (
            <p className="text-[14px] py-8 text-center" style={{ color: '#9B978E' }}>
              Loading conversations…
            </p>
          ) : chatSessions.length === 0 ? (
            <p className="text-[14px] py-8 text-center" style={{ color: '#9B978E' }}>
              No conversations yet — they'll appear here as your child chats with Luno.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {chatSessions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setOpenSessionId(s.id)}
                  className="flex items-center gap-3 w-full text-left rounded-xl px-4 py-3 transition hover:opacity-90"
                  style={{ background: '#F5F3EE' }}
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: '#E8F6F4' }}
                  >
                    <MessagesSquare size={16} style={{ color: '#3ECDC6' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] truncate" style={{ fontWeight: 500, color: '#2A2926' }}>
                      {s.title || 'Chat with Luno'}
                    </p>
                    <p className="text-[12px]" style={{ color: '#9B978E' }}>
                      {formatShortDate(s.last_message_at || s.started_at)} · {s.message_count}{' '}
                      {s.message_count === 1 ? 'message' : 'messages'}
                    </p>
                  </div>
                  <Eye size={15} style={{ color: '#9B978E' }} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mood Tab */}
      {activeTab === 'mood' && (
        <div
          className="rounded-2xl p-6 mb-8"
          style={{ background: '#FFFFFF', border: '1px solid #E8E6E1' }}
        >
          <h2 className="text-[20px] mb-1" style={{ fontWeight: 600, color: '#2A2926' }}>
            How they're feeling
          </h2>
          <p className="text-[14px] mb-6" style={{ color: '#9B978E' }}>
            Mood check-ins over the past 14 days
          </p>
          <div className="flex gap-2 overflow-x-auto pb-3 mb-6">
            {moodGrid.map((m, i) => (
              <div key={i} className="flex flex-col items-center gap-1.5 min-w-[44px]">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-[18px]"
                  style={{
                    background: m.isToday ? '#E8F6F4' : '#F5F3EE',
                    border: m.isToday ? '2px solid #3ECDC6' : '1px solid transparent',
                  }}
                >
                  {m.emoji || (
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ background: '#D4D2CD' }}
                    />
                  )}
                </div>
                <span className="text-[11px]" style={{ color: '#9B978E' }}>
                  {m.day}
                </span>
              </div>
            ))}
          </div>
          {moodStats ? (
            <div className="flex flex-col gap-2 mb-5">
              <p className="text-[14px]" style={{ color: '#2A2926' }}>
                Most common mood: {MOOD_EMOJI[moodStats.topMood]} {MOOD_LABEL[moodStats.topMood]}{' '}
                ({moodStats.topCount} of {moodStats.totalDays} days)
              </p>
              {moodStats.transitions.length > 1 && (
                <p className="text-[14px]" style={{ color: '#6B675E' }}>
                  Recent changes: {moodStats.transitions.join(' → ')}
                </p>
              )}
            </div>
          ) : (
            <p className="text-[14px] mb-5 italic" style={{ color: '#9B978E' }}>
              No check-ins yet. They'll appear when your child starts exploring.
            </p>
          )}
          <p className="text-[13px]" style={{ color: '#9B978E' }}>
            Mood patterns can reveal how your child is doing. If you notice changes,
            it might be a beautiful conversation to have together.
          </p>
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <div
          className="rounded-2xl p-6 mb-8"
          style={{ background: '#FFFFFF', border: '1px solid #E8E6E1' }}
        >
          <h2 className="text-[20px] mb-1" style={{ fontWeight: 600, color: '#2A2926' }}>
            Daily activity
          </h2>
          <p className="text-[14px] mb-5" style={{ color: '#9B978E' }}>
            What happened each day this week
          </p>
          {activityTimeline.length === 0 ? (
            <p className="text-[14px] py-8 text-center" style={{ color: '#9B978E' }}>
              Nothing yet — this fills up as your child discovers Yoluno.
            </p>
          ) : (
            <>
              {/* Mini bar chart — bar height = session minutes */}
              <div className="flex items-end gap-3 mb-6 h-[50px]">
                {activityTimeline
                  .slice()
                  .reverse()
                  .map((d, i) => {
                    const mins = d.session_duration_minutes;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div
                          className="w-full rounded-t"
                          style={{
                            height: mins > 0 ? `${(mins / maxMinutes) * 40}px` : 2,
                            background: mins > 0 ? '#3ECDC6' : '#E8E6E1',
                            minHeight: 2,
                          }}
                        />
                        <span className="text-[10px]" style={{ color: '#9B978E' }}>
                          {formatShortDay(d.date)}
                        </span>
                      </div>
                    );
                  })}
              </div>
              <div className="flex flex-col gap-0">
                {activityTimeline.map((d) => {
                  const hasActivity =
                    d.message_count > 0 ||
                    d.story_count > 0 ||
                    d.journey_steps_completed > 0;
                  return (
                    <div
                      key={d.date}
                      className="py-3 border-b last:border-b-0"
                      style={{ borderColor: '#F5F3EE' }}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                        <span
                          className="text-[14px] sm:w-[180px] shrink-0"
                          style={{ fontWeight: 600, color: '#2A2926' }}
                        >
                          {formatLongDay(d.date)}
                        </span>
                        {!hasActivity && d.session_duration_minutes === 0 ? (
                          <span className="text-[14px] italic" style={{ color: '#9B978E' }}>
                            No session
                          </span>
                        ) : (
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 flex-1">
                            <span className="text-[14px]" style={{ color: '#6B675E' }}>
                              {d.message_count} questions · {d.story_count} stories ·{' '}
                              {d.journey_steps_completed} journey steps
                            </span>
                            {d.topics && d.topics.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {d.topics.map((t) => (
                                  <span
                                    key={t}
                                    className="px-2.5 py-0.5 rounded-full text-[11px]"
                                    style={{ background: '#F5F3EE', color: '#9B978E' }}
                                  >
                                    {t}
                                  </span>
                                ))}
                              </div>
                            )}
                            {d.session_duration_minutes > 0 && (
                              <span
                                className="text-[12px] sm:ml-auto shrink-0"
                                style={{ color: '#9B978E' }}
                              >
                                {d.session_duration_minutes} min
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Journeys Tab */}
      {activeTab === 'journeys' && (
        <div
          className="rounded-2xl p-6 mb-8"
          style={{ background: '#FFFFFF', border: '1px solid #E8E6E1' }}
        >
          <h2 className="text-[20px] mb-1" style={{ fontWeight: 600, color: '#2A2926' }}>
            Journey progress
          </h2>
          <p className="text-[14px] mb-5" style={{ color: '#9B978E' }}>
            Where each journey stands
          </p>
          {journeyProgress.length === 0 ? (
            <p className="text-[14px] py-8 text-center" style={{ color: '#9B978E' }}>
              No journeys yet. Head to Journeys to start your child's first adventure.
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-4 mb-6">
                {activeJourneys.map((j, i) => {
                  const color = JOURNEY_PALETTE[i % JOURNEY_PALETTE.length];
                  return (
                    <div
                      key={j.id}
                      className="rounded-xl p-4"
                      style={{
                        border: '1px solid #E8E6E1',
                        borderLeft: `4px solid ${color}`,
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-[15px]"
                            style={{ fontWeight: 600, color: '#2A2926' }}
                          >
                            {j.title}
                          </span>
                          <span
                            className="text-[12px] px-2 py-0.5 rounded-full"
                            style={{ background: '#FEF0EA', color: '#E8946A' }}
                          >
                            In Progress
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div
                          className="flex-1 h-2 rounded-full"
                          style={{ background: '#F5F3EE' }}
                        >
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${j.progress_percent}%`,
                              background: color,
                            }}
                          />
                        </div>
                        <span
                          className="text-[12px] shrink-0"
                          style={{ color: '#9B978E' }}
                        >
                          Step {j.steps_completed} of {j.total_steps}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {completedJourneys.length > 0 && (
                <>
                  <h3
                    className="text-[15px] mb-3"
                    style={{ fontWeight: 600, color: '#9B978E' }}
                  >
                    Completed
                  </h3>
                  <div className="flex flex-col gap-2">
                    {completedJourneys.map((j) => (
                      <div
                        key={j.id}
                        className="flex items-center justify-between py-2"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="px-2.5 py-0.5 rounded-full text-[11px]"
                            style={{
                              background: '#E8F6F4',
                              color: '#3ECDC6',
                              fontWeight: 600,
                            }}
                          >
                            Complete
                          </span>
                          <span className="text-[14px]" style={{ color: '#2A2926' }}>
                            {j.title}
                          </span>
                          {j.completed_at && (
                            <span className="text-[12px]" style={{ color: '#9B978E' }}>
                              · {formatShortDate(j.completed_at)}
                            </span>
                          )}
                        </div>
                        <button
                          className="flex items-center gap-1 text-[12px] hover:underline"
                          style={{ color: '#3ECDC6', fontWeight: 600 }}
                        >
                          <Eye size={12} /> See reflections
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {activeJourneys.length === 0 && completedJourneys.length === 0 && (
                <p className="text-[14px] py-8 text-center" style={{ color: '#9B978E' }}>
                  No journeys underway yet.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Moments Tab */}
      {activeTab === 'moments' && (
        <div
          className="rounded-2xl p-6 mb-8"
          style={{ background: '#FFFFFF', border: '1px solid #E8E6E1' }}
        >
          <h2 className="text-[20px] mb-1" style={{ fontWeight: 600, color: '#2A2926' }}>
            Growth Journal
          </h2>
          <p className="text-[14px] mb-5" style={{ color: '#9B978E' }}>
            The moments that matter — each one a step in your child's story.
          </p>
          {childMoments.length === 0 ? (
            <p className="text-[14px] py-8 text-center" style={{ color: '#9B978E' }}>
              Nothing here yet — moments will appear as your child grows and shares.
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {childMoments.map((m) => {
                const mc = MOMENT_COLORS[m.moment_type] ?? MOMENT_COLORS.curiosity;
                return (
                  <div
                    key={m.id}
                    className="rounded-xl p-5"
                    style={{
                      border: '1px solid #E8E6E1',
                      borderLeft: `4px solid ${mc.border}`,
                    }}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <h4
                          className="text-[16px]"
                          style={{ fontWeight: 600, color: '#2A2926' }}
                        >
                          {m.title}
                        </h4>
                        <span className="text-[13px]" style={{ color: '#9B978E' }}>
                          {formatShortDate(m.shared_at)}
                        </span>
                      </div>
                      {!m.is_auto && (
                        <span
                          className="px-2.5 py-0.5 rounded-full text-[11px] whitespace-nowrap"
                          style={{
                            background: '#FDF6E8',
                            color: '#D4A843',
                            fontWeight: 600,
                          }}
                        >
                          Shared with you
                        </span>
                      )}
                    </div>
                    {m.context && (
                      <p className="text-[14px] mb-2" style={{ color: '#6B675E' }}>
                        {m.context}
                      </p>
                    )}
                    {m.reflection && (
                      <div className="rounded-lg px-4 py-3" style={{ background: '#F5F3EE' }}>
                        <p className="text-[14px] italic" style={{ color: '#6B675E' }}>
                          "{m.reflection}"
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Weekly Summary */}
      <div
        className="rounded-2xl p-6"
        style={{ background: '#F5F3EE', borderLeft: '3px solid #3ECDC6' }}
      >
        <h3 className="text-[18px] mb-3" style={{ fontWeight: 600, color: '#2A2926' }}>
          This week in a sentence
        </h3>
        <p
          className="text-[16px] mb-3"
          style={{ color: '#6B675E', lineHeight: 1.7 }}
        >
          {weeklySummarySentence}
        </p>
        <p className="text-[12px]" style={{ color: '#9B978E' }}>
          A gentle glimpse into your child's week — full details are in each section above.
        </p>
      </div>

      {/* Full session transcript */}
      {openSessionId && (
        <ConversationTranscriptModal
          messages={sessionMessages}
          title={openSession?.title || 'Chat with Luno'}
          subtitle={`${selectedChild?.name ?? 'Your child'}'s chat with Luno — for your eyes only`}
          isLoading={sessionMessagesLoading}
          emptyLabel="No messages in this chat yet."
          onClose={() => setOpenSessionId(null)}
        />
      )}

      {/* Topic drill-down: the actual questions behind a topic */}
      {drillTopic && (
        <ConversationTranscriptModal
          messages={topicMessages}
          title={`Questions about "${drillTopic}"`}
          subtitle={`What ${selectedChild?.name ?? 'your child'} actually asked Luno`}
          isLoading={topicMessagesLoading}
          emptyLabel={`No matching questions found for "${drillTopic}".`}
          onClose={() => setDrillTopic(null)}
        />
      )}
    </div>
  );
}
